'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Icon from "@/components/ui/AppIcon";
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

interface JobBlock {
  id: string;
  customer: string;
  time: string;
  duration: number;
  color: string;
  crew: string;
  day: number;
  assigned_pm_id?: string;
  end_date?: string | null;
}

interface WeatherData {
  condition: string;
  temp: string;
  icon: string;
  risk: 'low' | 'medium' | 'high';
}

// Build current week Mon–Fri dynamically
function getCurrentWeekDays(): { label: string; dateStr: string; isoDate: string }[] {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon...
  const monday = new Date(today);
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  monday.setDate(today.getDate() + diff);

  const result = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const label = d.toLocaleDateString('en-US', { weekday: 'short' });
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const isoDate = d.toISOString().split('T')[0];
    result.push({ label, dateStr, isoDate });
  }
  return result;
}

const DEMO_BLOCKS: JobBlock[] = [];

function mapRow(row: any): JobBlock {
  return {
    id: row.id,
    customer: row.customer,
    time: row.schedule_time,
    duration: row.duration,
    color: row.color,
    crew: row.crew,
    day: row.day_index,
    assigned_pm_id: row.assigned_pm_id,
    end_date: row.end_date ?? null,
  };
}

function getDeadlineDays(job: JobBlock): number | null {
  const endDate = job.end_date;
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function DeadlineBadge({ days }: { days: number }) {
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white">
        <Icon name="ExclamationCircleIcon" size={9} />
        {Math.abs(days)}d overdue
      </span>
    );
  }
  if (days === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-500 text-white">
        <Icon name="ClockIcon" size={9} />
        Due today
      </span>
    );
  }
  if (days <= 2) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-400 text-white">
        <Icon name="ClockIcon" size={9} />
        {days}d left
      </span>
    );
  }
  if (days <= 5) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-yellow-400 text-yellow-900">
        <Icon name="ClockIcon" size={9} />
        {days}d left
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-black/20 text-current">
      <Icon name="ClockIcon" size={9} />
      {days}d left
    </span>
  );
}

function WeatherBadge({ weather }: { weather: WeatherData }) {
  const riskColors = {
    low: 'bg-green-500/20 text-green-700 border-green-300',
    medium: 'bg-yellow-500/20 text-yellow-700 border-yellow-300',
    high: 'bg-red-500/20 text-red-700 border-red-300',
  };
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-caption ${riskColors[weather.risk]}`}>
      <span>{weather.icon}</span>
      <span>{weather.condition}</span>
      <span className="font-semibold">{weather.temp}</span>
      {weather.risk === 'high' && (
        <span className="font-bold text-red-600">⚠ Risk</span>
      )}
    </div>
  );
}

export default function CalendarWidget() {
  const { currentUser, isSuperAdmin } = useAuth();
  const weekDays = getCurrentWeekDays();

  // Default to today's weekday index (0=Mon...4=Fri), clamp to 0-4
  const todayDayOfWeek = new Date().getDay();
  const defaultDay = todayDayOfWeek === 0 ? 0 : todayDayOfWeek === 6 ? 4 : todayDayOfWeek - 1;

  const [activeDay, setActiveDay] = useState(Math.min(defaultDay, 4));
  const [jobBlocks, setJobBlocks] = useState<JobBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [weatherMap, setWeatherMap] = useState<Record<number, WeatherData>>({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherFetched, setWeatherFetched] = useState(false);
  const [showAllModal, setShowAllModal] = useState(false);

  useEffect(() => {
    const supabase = createClient();

    async function fetchSchedule() {
      try {
        let query = supabase.from('schedule_entries').select('*').order('schedule_time', { ascending: true });
        if (!isSuperAdmin) {
          query = query.eq('assigned_pm_id', currentUser.id);
        }
        const { data, error } = await query;
        if (error) {
          console.error('Schedule fetch error:', error.message);
          setJobBlocks(DEMO_BLOCKS);
          return;
        }
        const blocks = (data || []).map(mapRow);
        // Use demo data if DB has no entries
        setJobBlocks(blocks.length > 0 ? blocks : DEMO_BLOCKS);
      } catch {
        setJobBlocks(DEMO_BLOCKS);
      } finally {
        setLoading(false);
      }
    }

    fetchSchedule();

    const channel = supabase
      .channel('schedule_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'schedule_entries' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newBlock = mapRow(payload.new);
          if (isSuperAdmin || newBlock.assigned_pm_id === currentUser.id) {
            setJobBlocks((prev) => [...prev.filter(b => !b.id.startsWith('demo-')), newBlock]);
          }
        } else if (payload.eventType === 'UPDATE') {
          setJobBlocks((prev) => prev.map((j) => j.id === payload.new.id ? mapRow(payload.new) : j));
        } else if (payload.eventType === 'DELETE') {
          setJobBlocks((prev) => prev.filter((j) => j.id !== (payload.old as any).id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser.id, isSuperAdmin]);

  // Fetch weather via OpenAI for days that have jobs
  const fetchWeather = useCallback(async () => {
    if (weatherFetched || weatherLoading) return;
    setWeatherLoading(true);
    setWeatherFetched(true);
    try {
      const scheduledDays = [...new Set(jobBlocks.map(j => j.day))];
      if (scheduledDays.length === 0) return;

      const dateList = scheduledDays.map(d => `Day ${d} (${weekDays[d]?.dateStr ?? d})`).join(', ');
      const response = await getChatCompletion(
        'GEMINI',
        'gemini/gemini-2.5-flash',
        [
          {
            role: 'system',
            content: 'You are a weather assistant. Return ONLY valid JSON, no markdown, no explanation.',
          },
          {
            role: 'user',
            content: `Generate realistic weather forecasts for roofing install days in a mid-US city for: ${dateList}. 
Return JSON object with day index as key (0-4). Each value: { condition: string (2-3 words), temp: string (e.g. "72°F"), icon: string (single weather emoji), risk: "low"|"medium"|"high" }. 
Risk "high" = rain/storm/snow/wind >20mph. Risk "medium" = cloudy/light wind. Risk "low" = clear/sunny.
Example: {"1": {"condition": "Partly Cloudy", "temp": "68°F", "icon": "⛅", "risk": "low"}}`,
          },
        ],
        { max_completion_tokens: 300 }
      );

      const content = response?.choices?.[0]?.message?.content ?? '';
      const parsed = JSON.parse(content);
      const result: Record<number, WeatherData> = {};
      for (const [key, val] of Object.entries(parsed)) {
        result[parseInt(key)] = val as WeatherData;
      }
      setWeatherMap(result);
    } catch {
      // Silently fail — weather is supplemental
    } finally {
      setWeatherLoading(false);
    }
  }, [jobBlocks, weatherFetched, weatherLoading, weekDays]);

  useEffect(() => {
    if (!loading && jobBlocks.length > 0 && !weatherFetched) {
      fetchWeather();
    }
  }, [loading, jobBlocks, weatherFetched, fetchWeather]);

  const dayJobs = jobBlocks.filter(j => j.day === activeDay);
  const previewJobs = dayJobs.slice(0, 3);
  const hasMore = dayJobs.length > 3;

  // Conflict detection: find crews that appear more than once on the active day
  const crewCounts: Record<string, number> = {};
  dayJobs.forEach(j => {
    if (j.crew) crewCounts[j.crew] = (crewCounts[j.crew] ?? 0) + 1;
  });
  const doubleBookedCrews = new Set(
    Object.entries(crewCounts).filter(([, count]) => count > 1).map(([crew]) => crew)
  );

  // Count conflicts per day tab
  const dayConflictMap: Record<number, boolean> = {};
  weekDays.forEach((_, i) => {
    const dJobs = jobBlocks.filter(j => j.day === i);
    const cc: Record<string, number> = {};
    dJobs.forEach(j => { if (j.crew) cc[j.crew] = (cc[j.crew] ?? 0) + 1; });
    dayConflictMap[i] = Object.values(cc).some(c => c > 1);
  });

  const activeWeather = weatherMap[activeDay];

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon name="CalendarDaysIcon" size={18} className="text-secondary" variant="solid" />
          <h3 className="font-heading text-base font-semibold text-foreground">Schedule</h3>
          {!loading && (
            <span className="flex items-center gap-1 text-[10px] font-caption text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </span>
          )}
          {!isSuperAdmin && (
            <span className="flex items-center gap-1 text-[10px] font-caption text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              <Icon name="UserIcon" size={10} />
              Mine
            </span>
          )}
          {doubleBookedCrews.size > 0 && (
            <span className="flex items-center gap-1 text-[10px] font-caption text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
              <Icon name="ExclamationTriangleIcon" size={10} />
              {doubleBookedCrews.size} conflict{doubleBookedCrews.size > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <Link
          href="/interactive-calendar-schedule-management"
          className="flex items-center gap-1 font-caption text-xs text-primary hover:text-secondary transition-standard"
        >
          Full Calendar
          <Icon name="ArrowTopRightOnSquareIcon" size={12} />
        </Link>
      </div>

      {/* Day tabs */}
      <div className="flex border-b border-border flex-shrink-0">
        {weekDays.map((day, i) => (
          <button
            key={day.isoDate}
            onClick={() => setActiveDay(i)}
            className={`flex-1 py-2.5 flex flex-col items-center relative transition-standard ${
              activeDay === i
                ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <span className="font-caption text-xs">{day.label}</span>
            <span className={`font-heading text-sm font-semibold mt-0.5 ${activeDay === i ? 'text-primary' : ''}`}>
              {day.dateStr.split(' ')[1]}
            </span>
            {/* Conflict dot on tab */}
            {dayConflictMap[i] && (
              <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500" title="Crew conflict" />
            )}
          </button>
        ))}
      </div>

      {/* Weather strip for active day */}
      {activeWeather && (
        <div className="px-4 pt-2 pb-1 flex items-center gap-2 flex-shrink-0">
          <Icon name="SunIcon" size={12} className="text-muted-foreground" />
          <span className="font-caption text-[10px] text-muted-foreground uppercase tracking-wide">Forecast</span>
          <WeatherBadge weather={activeWeather} />
        </div>
      )}
      {weatherLoading && !activeWeather && (
        <div className="px-4 pt-2 pb-1 flex items-center gap-1.5 flex-shrink-0">
          <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <span className="font-caption text-[10px] text-muted-foreground">Loading forecast...</span>
        </div>
      )}

      {/* Job blocks — preview only (max 3) */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin mb-2" />
            <p className="font-body text-xs text-muted-foreground">Loading schedule...</p>
          </div>
        ) : dayJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <Icon name="CalendarIcon" size={32} className="text-muted-foreground mb-2" />
            <p className="font-body text-sm text-muted-foreground">No jobs scheduled</p>
          </div>
        ) : (
          <>
            {previewJobs.map((job) => {
              const isConflict = doubleBookedCrews.has(job.crew);
              const deadlineDays = getDeadlineDays(job);
              return (
                <Link
                  key={job.id}
                  href="/interactive-calendar-schedule-management"
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-lg hover:opacity-90 transition-standard press-scale block relative ${
                    isConflict
                      ? 'ring-2 ring-red-500 ring-offset-1 bg-red-50 border border-red-200'
                      : job.color
                  }`}
                >
                  {isConflict && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-red-500" />
                  )}
                  <div className="flex-1 min-w-0 pl-1">
                    <div className="flex items-center justify-between gap-1 flex-wrap">
                      <span className={`font-heading text-sm font-semibold truncate ${isConflict ? 'text-red-800' : ''}`}>
                        {job.customer}
                      </span>
                      <span className={`font-data text-xs opacity-80 flex-shrink-0 ${isConflict ? 'text-red-600' : ''}`}>
                        {job.id.startsWith('demo-') ? '' : job.id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`font-caption text-xs flex items-center gap-1 ${isConflict ? 'text-red-700 opacity-90' : 'opacity-90'}`}>
                        <Icon name="ClockIcon" size={10} />
                        {job.time} · {job.duration}h
                      </span>
                      <span className={`font-caption text-xs flex items-center gap-1 ${isConflict ? 'text-red-700 opacity-90' : 'opacity-90'}`}>
                        <Icon name="UserGroupIcon" size={10} />
                        {job.crew}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {isConflict && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white">
                          <Icon name="ExclamationTriangleIcon" size={9} />
                          Double-booked
                        </span>
                      )}
                      {deadlineDays !== null && (
                        <DeadlineBadge days={deadlineDays} />
                      )}
                    </div>
                  </div>
                  <Icon name="ChevronRightIcon" size={14} className={`opacity-70 flex-shrink-0 mt-1 ${isConflict ? 'text-red-500' : ''}`} />
                </Link>
              );
            })}
            {hasMore && (
              <button
                onClick={() => setShowAllModal(true)}
                className="w-full py-2 rounded-lg border border-dashed border-border text-xs font-caption text-primary hover:bg-primary/5 hover:border-primary transition-standard flex items-center justify-center gap-1.5"
              >
                <Icon name="EyeIcon" size={13} />
                View all {dayJobs.length} jobs
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-border flex-shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-caption text-xs text-muted-foreground">
            {dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''} · {weekDays[activeDay]?.dateStr}
          </span>
          {doubleBookedCrews.size > 0 && (
            <span className="font-caption text-[10px] text-red-600 flex items-center gap-0.5">
              <Icon name="ExclamationCircleIcon" size={10} />
              {doubleBookedCrews.size} crew conflict{doubleBookedCrews.size > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setActiveDay(p => Math.max(0, p - 1))}
            className="w-7 h-7 rounded-md bg-muted hover:bg-border flex items-center justify-center transition-standard"
            disabled={activeDay === 0}
          >
            <Icon name="ChevronLeftIcon" size={14} className={activeDay === 0 ? 'text-muted-foreground/40' : 'text-foreground'} />
          </button>
          <button
            onClick={() => setActiveDay(p => Math.min(4, p + 1))}
            className="w-7 h-7 rounded-md bg-muted hover:bg-border flex items-center justify-center transition-standard"
            disabled={activeDay === 4}
          >
            <Icon name="ChevronRightIcon" size={14} className={activeDay === 4 ? 'text-muted-foreground/40' : 'text-foreground'} />
          </button>
        </div>
      </div>

      {/* View All Modal */}
      {showAllModal && (
        <>
          <div
            className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAllModal(false)}
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-lg max-h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Icon name="CalendarDaysIcon" size={18} className="text-secondary" variant="solid" />
                  <h3 className="font-heading text-base font-semibold text-foreground">
                    Schedule — {weekDays[activeDay]?.dateStr}
                  </h3>
                  <span className="font-caption text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  onClick={() => setShowAllModal(false)}
                  className="p-1.5 rounded-lg hover:bg-muted transition-standard"
                >
                  <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                </button>
              </div>
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {dayJobs.map((job) => {
                  const isConflict = doubleBookedCrews.has(job.crew);
                  const deadlineDays = getDeadlineDays(job);
                  return (
                    <Link
                      key={job.id}
                      href="/interactive-calendar-schedule-management"
                      onClick={() => setShowAllModal(false)}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg hover:opacity-90 transition-standard press-scale block relative ${
                        isConflict
                          ? 'ring-2 ring-red-500 ring-offset-1 bg-red-50 border border-red-200'
                          : job.color
                      }`}
                    >
                      {isConflict && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-red-500" />
                      )}
                      <div className="flex-1 min-w-0 pl-1">
                        <div className="flex items-center justify-between gap-1 flex-wrap">
                          <span className={`font-heading text-sm font-semibold truncate ${isConflict ? 'text-red-800' : ''}`}>
                            {job.customer}
                          </span>
                          <span className={`font-data text-xs opacity-80 flex-shrink-0 ${isConflict ? 'text-red-600' : ''}`}>
                            {job.id.startsWith('demo-') ? '' : job.id}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`font-caption text-xs flex items-center gap-1 ${isConflict ? 'text-red-700 opacity-90' : 'opacity-90'}`}>
                            <Icon name="ClockIcon" size={10} />
                            {job.time} · {job.duration}h
                          </span>
                          <span className={`font-caption text-xs flex items-center gap-1 ${isConflict ? 'text-red-700 opacity-90' : 'opacity-90'}`}>
                            <Icon name="UserGroupIcon" size={10} />
                            {job.crew}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {isConflict && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white">
                              <Icon name="ExclamationTriangleIcon" size={9} />
                              Double-booked
                            </span>
                          )}
                          {deadlineDays !== null && (
                            <DeadlineBadge days={deadlineDays} />
                          )}
                        </div>
                      </div>
                      <Icon name="ChevronRightIcon" size={14} className={`opacity-70 flex-shrink-0 mt-1 ${isConflict ? 'text-red-500' : ''}`} />
                    </Link>
                  );
                })}
              </div>
              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-border flex-shrink-0">
                <Link
                  href="/interactive-calendar-schedule-management"
                  className="w-full flex items-center justify-center gap-1.5 py-2 bg-secondary text-white rounded-lg font-caption text-xs font-medium hover:bg-accent transition-standard"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                  Open Full Calendar
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}